import { useEffect, useState } from "react";
import { useIntl, type IntlShape } from "react-intl";

export function formatDateTime(intl: IntlShape, date: Date | string) {
  if (!date) {
    return null;
  }

  return intl.formatDate(date, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function useRelativeTime(date?: string | Date) {
  const intl = useIntl();
  const [_, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  if (!date) return "";

  const target = new Date(date);
  const now = new Date();
  const diffSeconds = Math.floor((target.getTime() - now.getTime()) / 1000);

  const absDiff = Math.abs(diffSeconds);

  if (absDiff < 60) {
    return intl.formatRelativeTime(diffSeconds, "second", { numeric: "auto" });
  }

  if (absDiff < 3600) {
    const minutes = Math.floor(diffSeconds / 60);
    return intl.formatRelativeTime(minutes, "minute", { numeric: "auto" });
  }

  if (absDiff < 86400) {
    const hours = Math.floor(absDiff / 3600);
    const remainingMinutes = Math.floor((absDiff % 3600) / 60);

    if (remainingMinutes === 0) {
      return intl.formatMessage(
        { defaultMessage: "{hours} hours ago" },
        { hours },
      );
    } else {
      return intl.formatMessage(
        { defaultMessage: "{hours} hours {minutes} minutes ago" },
        { hours, minutes: remainingMinutes },
      );
    }
  }

  const days = Math.floor(absDiff / 86400);
  const remainingHours = Math.floor((absDiff % 86400) / 3600);

  if (remainingHours === 0) {
    return intl.formatMessage({ defaultMessage: "{days} days ago" }, { days });
  } else {
    return intl.formatMessage(
      { defaultMessage: "{days} days {hours} hours ago" },
      { days, hours: remainingHours },
    );
  }
}
